import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import "./navigation.scss";
import { AuthContext } from "../Context/AuthContext";
import { currentTeamId, getUserIndustry, hasClient } from "../Utils/utils";
import { Navbar, Nav, NavDropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  prefetchAutomationRoutes,
  prefetchBuilderRoutes,
  prefetchMarketingRoutes,
  prefetchScheduleRoutes,
} from "../Utils/routePrefetch";
import { ANNOUNCEMENTS } from "../GraphApi/Announcement";
import { AnnouncementPreview } from "../Components/Pages/Settings/Announcements/AnnouncementPreview";
import Session from "../Utils/Session";
import { getSettings } from "../Utils/CurrentSetting";
import { equpIndustryInfo } from "../Utils/commonParams";
// import { checkIfPlanExpired } from "../Context/TrialPlanChecker";

const urlSchema = {
  crm: ["crm", "contact-details", "company-management", "company-details"],
  dashboard: [
    "dashboard",
    "edit-dashboard",
    "new-dashboard",
    "dashboard/admin",
    "dashboard/technician",
    "dashboard/sales",
    "dashboard/marketing",
  ],
  contact: ["crm", "contact-details"],
  owners: ["owners"],
  company: ["company-management", "company-details"],
  sales: [
    "sales/pipeline",
    "sales/service-request",
    "sales/agreements",
    "sales/estimates",
    "sales/invoices",
  ],
  jobs: [
    "jobs",
    "create-job",
    "estimates",
    "jobs/service-visit",
    "jobs/schedule",
    "jobs/timesheets",
    "jobs/dispatcher",
    "jobs/dispatcher-old",
    "dispatcher-v2",
    "jobs/smart-scheduler",
    "jobs/auto-scheduler",
    "jobs/live-tracking",
    "jobs/live-map",
    "service-visits",
  ],
  automation: ["automation", "automation/create", "campaign-analytics"],
  marketing: [
    "marketing/landing-pages",
    "marketing/emails",
    "marketing/forms",
    "lead-capture/meta/forms",
    "lead-capture/linkedin/forms",
    "marketing/chatbot",
    "marketing/whatsapp-management",
    "social-media",
    "social-media/add-account",
    "social-media/social-post",
    "social-media/monitoring",
    "marketing/sms-management",
  ],
  engagements: ["engagements"],
  scheduling: [
    "schedule/my-schedule",
    "schedule/service-request",
    "schedule/appointments",
    "schedule/tasks",
  ],
  catalog: [
    "services",
    "subscriptions",
    "ecommerce",
    "subscription",
    "add-plan",
    "add-layout",
  ],
  profile: ["my-profile"],
  settings: ["settings"],
  social: [
    "social-media",
    "social-media/add-account",
    "social-media/social-post",
    "social-media/monitoring",
  ],
};

const jobsNavigationGroups = [
  {
    label: "",
    items: [
      { label: "View Jobs", to: "/jobs", match: ["jobs"] },
      {
        label: "Schedule Jobs",
        to: "/jobs/schedule",
        match: [
          "jobs/schedule",
          "jobs/dispatcher",
          "jobs/dispatcher-old",
          "dispatcher-v2",
          "service-visits",
        ],
      },
      {
        label: "Smart Scheduler",
        to: "/jobs/smart-scheduler",
        match: ["jobs/smart-scheduler", "jobs/auto-scheduler"],
        permission: "smart_scheduler:view",
      },
      {
        label: "Live Tracking",
        to: "/jobs/live-tracking",
        match: ["jobs/live-tracking", "jobs/live-map"],
        permission: "live_tracking:view",
      },
      {
        label: "Timesheets",
        to: "/jobs/timesheets",
        match: ["jobs/timesheets"],
        permission: "timesheet:view",
      },
    ],
  },
];

const Navigation = ({
  expanded = false,
  setExpanded = () => {},
  embedded = false,
}) => {
  const { pathname, state: routeState } = useLocation();
  const { t: locale } = useTranslation(["common", "pages"]);
  const [industryInfo, setIndustryInfo] = useState(equpIndustryInfo);
  const [industry, setIndustry] = useState("");
  const { userData } = useContext(AuthContext);

  const [currentCompanySetting, setCurrentCompanySetting] = useState(null);
  const [savedPin, setSavedPin] = useState(null);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [pathName, setPathName] = useState("");
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    if (pathname) {
      setPathName(pathname.replace("/", ""));
    }
  }, [pathname]);

  useEffect(() => {
    if (userData?.authData) {
      setPlanData(userData?.authData?.subscription || null);
      const userIndustry = getUserIndustry(userData);
      if (userIndustry) {
        setIndustry(userIndustry);
        setIndustryInfo({
          ...equpIndustryInfo,
          [userIndustry]: true,
        });

        document.title = userData?.authData?.company || "FieldServicePro";
      }

      setSavedPin(userData?.authData?.pin_data || null);
      if (userData?.authData?.currentTeam?.settings) {
        const companySettings = getSettings(
          userData.authData.currentTeam.settings,
        );
        setCurrentCompanySetting(companySettings);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const updateBodyClass = () => {
    if (document.body.classList.contains("modal-open")) {
      document.body.classList.remove("modal-open");
    } else {
      document.body.classList.add("modal-open");
    }
  };

  const navigationDrawerHide = () => {
    if (embedded) {
      setExpanded(false);
      return;
    }
    if (document.body.clientWidth <= 991) {
      setExpanded(expanded ? false : "expanded");
      updateBodyClass();
    }
  };

  /**
   * Show Navigation Menu Items
   **/
  const showMenuItems = (name) => {
    return true;
  };

  const getJobsActiveKey = () => {
    if (pathName.startsWith("jobs/schedule")) {
      return "jobs/schedule";
    }

    if (pathName.startsWith("jobs/dispatcher")) {
      return "jobs/schedule";
    }

    if (pathName.startsWith("jobs/timesheets")) {
      return "jobs/timesheets";
    }

    if (
      pathName.startsWith("jobs/dispatcher-old") ||
      pathName.startsWith("dispatcher-v2") ||
      pathName.startsWith("service-visits/")
    ) {
      return "jobs/schedule";
    }

    if (pathName.startsWith("jobs/smart-scheduler")) {
      return "jobs/smart-scheduler";
    }

    if (pathName.startsWith("jobs/auto-scheduler")) {
      return "jobs/smart-scheduler";
    }

    if (pathName.startsWith("jobs/live-tracking")) {
      return "jobs/live-tracking";
    }

    if (pathName.startsWith("jobs/live-map")) {
      return "jobs/live-tracking";
    }

    if (pathName === "create-job" || /^jobs\/[^/]+\/form$/.test(pathName)) {
      return "create-job";
    }

    if (pathName === "jobs" || /^jobs\/[^/]+\/detail$/.test(pathName)) {
      return "jobs";
    }

    return pathName;
  };

  const jobsActiveKey = getJobsActiveKey();
  const isJobsSectionActive = [
    "jobs",
    "create-job",
    "jobs/schedule",
    "jobs/smart-scheduler",
    "jobs/live-tracking",
  ].includes(jobsActiveKey);

  const NavigationOuter = embedded ? React.Fragment : Navbar;
  const NavigationInner = embedded ? React.Fragment : Navbar.Collapse;

  /*******/
  return (
    <React.Fragment>
      <NavigationOuter
        {...(!embedded
          ? {
              bg: "white",
              variant: "light",
              expand: "lg",
              expanded,
              className: hasClient(userData)
                ? "justify-content-between position-sticky top-0"
                : "position-sticky top-0",
              style: { zIndex: 998 },
            }
          : {})}
      >
        <NavigationInner
          {...(!embedded
            ? { id: "basic-navbar-nav", className: "primary-navbar" }
            : {})}
        >
          <Nav className={embedded ? "embedded-primary-navbar" : ""}>
            <Link
              to="/dashboard"
              state={{ industry: industry }}
              className={`nav-link ${
                urlSchema["dashboard"].includes(pathName) ? "active" : ""
              }`}
              role="button"
              onClick={() => navigationDrawerHide()}
            >
              {locale("pages:navigation.Dashboard")}
            </Link>
            {!hasClient(userData) && (
              <React.Fragment>
                <NavDropdown
                  title={locale("pages:navigation.CRM")}
                  renderMenuOnMount={true}
                  className={
                    urlSchema["crm"].includes(pathName) ? "active" : ""
                  }
                >
                  <Link
                    to="/crm"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      urlSchema["contact"].includes(pathName) ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Contacts")}
                  </Link>

                  <Link
                    to="/company-management"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      urlSchema["company"].includes(pathName) ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Companies")}
                  </Link>
                </NavDropdown>

                <NavDropdown
                  title={locale("pages:navigation.Sales")}
                  renderMenuOnMount={true}
                  className={
                    urlSchema["sales"].includes(pathName) ? "active" : ""
                  }
                >
                  <Link
                    to="/sales/pipeline"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "sales/pipeline" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Sales Pipeline")}
                  </Link>
                  <Link
                    to="/sales/agreements"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      pathName === "sales/agreements" ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Agreements")}
                  </Link>
                  <Link
                    to="/sales/estimates"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "sales/estimates" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Estimates")}
                  </Link>
                  <Link
                    to="/sales/invoices"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "invoices" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Invoices")}
                  </Link>
                </NavDropdown>
<NavDropdown
                  title="Scheduling"
                  renderMenuOnMount={true}
                  className={
                    urlSchema["scheduling"].includes(pathName) ? "active" : ""
                  }
                  onMouseEnter={prefetchScheduleRoutes}
                >
                  <Link
                    to="/schedule/my-schedule"
                    state={{ industry: industry }}
                    className={`dropdown-item ${pathName === "my-schedule" ? "active" : ""}`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    My Schedule
                  </Link>

                  <Link
                    to="/schedule/service-request"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "schedule/service-request" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Service Request")}
                  </Link>
                  <Link
                    to="/schedule/appointments"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "schedule/appointments" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Appointments
                  </Link>
                  <Link
                    to="/schedule/tasks"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "schedule/tasks" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Tasks
                  </Link>
                </NavDropdown>
                <Link
                  to="/engagements"
                  state={{ industry: industry }}
                  className={`nav-link ${
                    urlSchema["engagements"].indexOf(pathName) > -1
                      ? "active"
                      : ""
                  }`}
                  role="button"
                  onClick={() => navigationDrawerHide()}
                >
                  {locale("pages:navigation.engagements")}
                </Link>

                <NavDropdown
                  title={locale("pages:navigation.Jobs")}
                  renderMenuOnMount={true}
                  className={isJobsSectionActive ? "active" : ""}
                >
                  {jobsNavigationGroups.map((group, groupIndex) => (
                    <React.Fragment key={group.label}>
                      {groupIndex > 0 && <NavDropdown.Divider />}
                      {group.label ? (
                        <NavDropdown.Header className="jobs-nav-group-label">
                          {group.label}
                        </NavDropdown.Header>
                      ) : null}
                      {group.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          state={{ industry: industry }}
                          className={`dropdown-item ${
                            item.match.includes(jobsActiveKey) ? "active" : ""
                          }`}
                          role="button"
                          onClick={() => navigationDrawerHide()}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </React.Fragment>
                  ))}
                </NavDropdown>

                <NavDropdown
                 title={locale("pages:navigation.Catalog")}
                 renderMenuOnMount={true}
                  className={`catalog-nav-dropdown ${
                    urlSchema["catalog"].indexOf(pathName) > -1 ? "active" : ""
                  }`}
                >
                  <Link
                    to="/services"
                    state={{ industry: industry, tab: "offerings" }}
                    className={`dropdown-item ${
                      (routeState?.tab || "offerings") === "offerings"
                        ? "active"
                        : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Services
                  </Link>
                  <Link
                    to="/services"
                    state={{ industry: industry, tab: "products" }}
                    className={`dropdown-item ${
                      routeState?.tab === "products" ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Inventory
                  </Link>
                  <Link
                    to="/services"
                    state={{ industry: industry, tab: "assets" }}
                    className={`dropdown-item ${
                      routeState?.tab === "assets" ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Assets
                  </Link>
                  <Link
                    to="/services"
                    state={{ industry: industry, tab: "coupon" }}
                    className={`dropdown-item ${
                      routeState?.tab === "coupon" ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Coupon
                  </Link>
                </NavDropdown>

                <Link
                  to="/automation"
                  state={{ industry: industry }}
                  className={`nav-link ${
                    urlSchema["automation"].includes(pathName) ? "active" : ""
                  }`}
                  role="button"
                  onMouseEnter={prefetchAutomationRoutes}
                  onClick={() => navigationDrawerHide()}
                >
                  {locale("pages:navigation.Automation")}
                </Link>

                <NavDropdown
                    title={locale("pages:navigation.Marketing")}
                    renderMenuOnMount={true}
                    className={
                      urlSchema["marketing"].includes(pathName) ? "active" : ""
                    }
                    onMouseEnter={prefetchMarketingRoutes}
                  >
                  <Link
                    to="/marketing/emails"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "/marketing/emails" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onMouseEnter={prefetchBuilderRoutes}
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Emails")}
                  </Link>
                  <Link
                    to="/marketing/landing-pages"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "/marketing/landing-pages" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onMouseEnter={prefetchBuilderRoutes}
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Landing Pages")}
                  </Link>
                  <Link
                    to="/marketing/forms"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "/marketing/forms" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Forms")}
                  </Link>
                  <Link
                    to="/lead-capture/meta/forms"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      pathName.includes("lead-capture/meta/forms")
                        ? "active"
                        : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    Meta Lead Forms
                  </Link>
                  <Link
                    to="/lead-capture/linkedin/forms"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      pathName.includes("lead-capture/linkedin/forms")
                        ? "active"
                        : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    LinkedIn Lead Forms
                  </Link>
                  <Link
                    to="/marketing/chatbot"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "/marketing/chatbot" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Chatbot")}
                  </Link>{" "}
                  <Link
                    to="/marketing/whatsapp-management"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "/marketing/whatsapp-management" === pathName
                        ? "active"
                        : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.Whatsapp Management")}
                  </Link>{" "}
                  <Link
                    to="/marketing/sms-management"
                    state={{ industry: industry }}
                    className={`dropdown-item ${
                      "marketing/sms-management" === pathName ? "active" : ""
                    }`}
                    role="button"
                    onClick={() => navigationDrawerHide()}
                  >
                    {locale("pages:navigation.SMS Management")}
                  </Link>{" "}
                  {showMenuItems("social_media") && (
                    <NavDropdown
                      title={locale("pages:navigation.Social Media")}
                      id="submenu-nav-dropdown"
                      className={`social-media-nav-dropdown ${
                        urlSchema["social"].indexOf(pathName) > -1 ? "active" : ""
                      }`}
                    >
                      <Link
                        to="/social-media/add-account"
                        state={{ industry: industry }}
                        className={`dropdown-item ${
                          ["social-media/add-account"].indexOf(pathName) > -1
                            ? "active"
                            : ""
                        }`}
                        role="button"
                        onClick={() => navigationDrawerHide()}
                      >
                        {locale("pages:navigation.Add Account")}
                      </Link>
                      <Link
                        to="/social-media/social-post"
                        state={{ industry: industry }}
                        className={`dropdown-item ${
                          ["social-media/social-post"].indexOf(pathName) > -1
                            ? "active"
                            : ""
                        }`}
                        role="button"
                        onClick={() => navigationDrawerHide()}
                      >
                        {locale("pages:navigation.Schedule Posts")}
                      </Link>
                      <Link
                        to="/social-media/monitoring"
                        state={{ industry: industry }}
                        className={`dropdown-item ${
                          ["social-media/monitoring"].indexOf(pathName) > -1
                            ? "active"
                            : ""
                        }`}
                        role="button"
                        onClick={() => navigationDrawerHide()}
                      >
                        {locale("pages:navigation.Monitor Accounts")}
                      </Link>
                    </NavDropdown>
                  )}
                </NavDropdown>
              </React.Fragment>
            )}
          </Nav>
        </NavigationInner>{" "}
      </NavigationOuter>
    </React.Fragment>
  );
};

export default Navigation;
